import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  Skeleton,
  Divider,
  Tooltip,
} from '@mui/material';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import mermaid from 'mermaid';
import api from '../../../services/api';

// Reference page for the admin team. Explicitly NOT a workflow surface — the
// dashboard is the place to act. This page exists so a new admin can open
// it and quickly understand:
//   * the legal states a vendor can be in
//   * how long they can stay in each state before something happens
//   * which dashboard tab/action the admin should reach for
//
// We render the state machine as a real Mermaid diagram so it stays
// in-sync with the conceptual model in the migration / cleanup job.

const DIAGRAM_DEFINITION = `stateDiagram-v2
    direction LR
    [*] --> profileDraft: signup
    profileDraft --> agreement: profile saved
    agreement --> boothPick: agreement accepted
    boothPick --> softHeld: booth selected
    softHeld --> softHeld: any activity
    softHeld --> expiredHold: idle 60m
    expiredHold --> [*]: vendor notified
    softHeld --> pendingApproval: submit-for-approval
    pendingApproval --> approved: admin approves
    pendingApproval --> denied: admin denies
    approved --> complete: payment in 14d
    approved --> pendingApproval: 14d no payment
    denied --> [*]
    complete --> [*]
`;

mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  flowchart: { useMaxWidth: true, htmlLabels: true },
  securityLevel: 'strict',
});

const FlowDiagram = () => {
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const id = `registration-flow-diagram-${Math.random().toString(36).slice(2)}`;
    mermaid
      .render(id, DIAGRAM_DEFINITION)
      .then(({ svg }) => {
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      })
      .catch((err) => {
        // Don't crash the whole page if Mermaid fails — fall back to a
        // plain message; the cards below still convey the state machine.
        if (!cancelled && ref.current) {
          ref.current.innerHTML = '';
        }
        // eslint-disable-next-line no-console
        console.warn('Failed to render registration flow diagram', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        overflowX: 'auto',
        '& svg': {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          margin: '0 auto',
        },
      }}
    />
  );
};

const StateCard = ({
  icon,
  label,
  count,
  loading,
  description,
  ttl,
  triggers,
  exits,
  link,
  linkLabel,
  accent,
}) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      {loading ? (
        <Skeleton width={32} height={28} />
      ) : (
        <Chip
          size="small"
          label={count ?? 0}
          color={count > 0 ? accent || 'default' : 'default'}
        />
      )}
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
      {description}
    </Typography>
    {ttl ? (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Chip size="small" variant="outlined" label={ttl} />
      </Stack>
    ) : null}
    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
      Enters when
    </Typography>
    <Box component="ul" sx={{ pl: 2, mt: 0, mb: 1 }}>
      {triggers.map((t) => (
        <li key={t}>
          <Typography variant="body2" color="text.secondary">
            {t}
          </Typography>
        </li>
      ))}
    </Box>
    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
      Leaves when
    </Typography>
    <Box component="ul" sx={{ pl: 2, mt: 0, mb: 1.25 }}>
      {exits.map((t) => (
        <li key={t}>
          <Typography variant="body2" color="text.secondary">
            {t}
          </Typography>
        </li>
      ))}
    </Box>
    {link ? (
      <Button
        size="small"
        component={RouterLink}
        to={link}
        endIcon={<OpenInNewIcon fontSize="small" />}
      >
        {linkLabel || 'View'}
      </Button>
    ) : null}
  </Paper>
);

const RegistrationFlowPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/registration-flow/summary');
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to load registration flow summary', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const states = summary?.states || {};
  const thresholds = summary?.thresholds || {};

  const cards = useMemo(
    () => [
      {
        key: 'profile',
        icon: <EditNoteIcon color="action" />,
        label: 'Profile draft',
        count: states.profile_abandoners?.vendors,
        accent: 'error',
        description:
          'Vendor signed up and started filling in their profile or agreement, but has not yet picked a booth.',
        ttl: `Flagged after ${thresholds.profile_abandon_hours ?? 24}h of no activity`,
        triggers: [
          'Vendor completes signup',
          'Vendor saves profile or accepts the handmade agreement',
        ],
        exits: [
          'Vendor selects a booth on the map (moves to Soft held)',
          'Vendor never returns — admin can nudge or leave for re-engagement',
        ],
        link: '/admin/approvals?status=incomplete',
        linkLabel: 'Open Incomplete tab',
      },
      {
        key: 'soft_held',
        icon: <HourglassBottomIcon color="info" />,
        label: 'Soft held',
        count: states.soft_held?.vendors,
        accent: 'info',
        description:
          'Vendor picked a booth but has not submitted their application yet. The booth is reserved against other vendors but auto-releases on idle.',
        ttl: `Auto-releases after ${thresholds.soft_hold_ttl_minutes ?? 60} idle minutes`,
        triggers: ['Vendor clicks Continue on the booth selection step'],
        exits: [
          'Vendor submits for approval (moves to Pending approval)',
          'Vendor goes idle for the full TTL (moves to Expired hold)',
          'Vendor or admin manually releases the booth',
        ],
        link: '/admin/approvals?status=incomplete',
        linkLabel: 'See active soft holds',
      },
      {
        key: 'pending',
        icon: <PendingActionsIcon color="warning" />,
        label: 'Pending approval',
        count: states.pending_approval?.vendors,
        accent: 'warning',
        description:
          'Application has been submitted. Booth is firmly held. There is no automatic expiry — admins drive this queue with approve/deny/nudge.',
        ttl: `Flagged stale after ${thresholds.pending_nudge_days ?? 3} days`,
        triggers: ['Vendor submits-for-approval from the wizard'],
        exits: [
          'Admin approves (moves to Awaiting payment)',
          'Admin denies (moves to Denied)',
          'Admin manually releases the booth',
        ],
        link: '/admin/approvals?status=pending',
        linkLabel: 'Open Pending tab',
      },
      {
        key: 'approved',
        icon: <PaymentIcon color="success" />,
        label: 'Awaiting payment',
        count: states.approved_unpaid?.vendors,
        accent: 'success',
        description:
          'Admin approved the vendor. They have a 14-day window to pay before the booth bounces back to Pending approval automatically.',
        ttl: `${thresholds.approved_payment_window_days ?? 14}-day payment window`,
        triggers: ['Admin clicks Approve on a Pending vendor'],
        exits: [
          'Vendor pays via Stripe (moves to Complete)',
          'Window lapses without payment (returns to Pending approval)',
          'Admin manually releases the booth',
        ],
        link: '/admin/payments',
        linkLabel: 'Open Manage Billing',
      },
      {
        key: 'complete',
        icon: <CheckCircleIcon color="success" />,
        label: 'Complete',
        count: states.complete?.vendors,
        accent: 'success',
        description: 'Payment succeeded. The vendor is locked in and will receive final-confirmation emails.',
        ttl: 'No expiry',
        triggers: ['Stripe webhook reports payment succeeded'],
        exits: ['Manual admin reset (rare)'],
        link: '/admin/final-registrations',
        linkLabel: 'Open Final Registrations',
      },
      {
        key: 'denied',
        icon: <CancelIcon color="error" />,
        label: 'Denied',
        count: 0, // Surface from /vendors/denied if/when needed
        loading: false,
        description: 'Admin denied the application. Booth is freed. Vendor receives a denial email.',
        ttl: 'No expiry',
        triggers: ['Admin denies a Pending vendor'],
        exits: ['Admin re-enrolls the vendor (rare)'],
        link: '/admin/approvals?status=denied',
        linkLabel: 'Open Denied tab',
      },
      {
        key: 'expired',
        icon: <LinkOffIcon color="action" />,
        label: 'Expired hold',
        count: states.expired?.booths,
        description:
          'A soft hold or admin release. Kept as audit history; the booth is free for someone else and the vendor can restart.',
        ttl: 'Audit-only',
        triggers: [
          'Soft hold idle TTL elapses',
          'Vendor abandons their registration',
          'Admin releases a booth with a reason',
        ],
        exits: ['Vendor selects a different booth (new soft hold row)'],
        link: '/admin/approvals?status=incomplete',
        linkLabel: 'See expired holds',
      },
    ],
    [states, thresholds]
  );

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Registration Flow
          </Typography>
          <Tooltip title="This page is a reference. Manage vendors from the dashboard or the Approvals page.">
            <Chip size="small" variant="outlined" label="Reference" />
          </Tooltip>
        </Stack>
        <Button onClick={() => navigate('/admin')} startIcon={<OpenInNewIcon />}>
          Back to dashboard
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
          Two-tier reservation model
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Vendors get a short, soft hold while they finish onboarding (auto-releases on idle), then a firm hold once they submit for review. Approved vendors have a {thresholds.approved_payment_window_days ?? 14}-day window to pay before the booth is recycled.
        </Typography>
        <FlowDiagram />
      </Paper>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
        States at a glance
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <StateCard key={card.key} loading={loading && !summary} {...card} />
        ))}
      </Box>
    </Box>
  );
};

export default RegistrationFlowPage;
